import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import type { MultiChoiceQuizData, QuizSolveResult } from '../../../types/legacyQuiz'

export interface UseMultiChoiceQuizOptions {
  quizId: string | number
  quiz: MultiChoiceQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

export function useMultiChoiceQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseMultiChoiceQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const isSolved = solveResult !== null && solveResult !== undefined
  const answerCount = quiz.answerCount || 1
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers
  const hasInitialAnswers = initialAnswersProp !== undefined
  const initialAnswersKey = JSON.stringify(initialAnswersProp ?? [])
  const correctAnswersKey = JSON.stringify(solveResult?.correctAnswers ?? [])

  const sortedAnswers = useMemo(() => {
    if (quiz.random && Array.isArray(quiz.random) && quiz.random.length > 0) {
      return quiz.random.map(idx => quiz.choices[idx]).filter(Boolean)
    }
    return quiz.choices
  }, [quiz.choices, quiz.random])

  const choiceCorrectKeys = useMemo(() => {
    return quiz.choices.filter(a => a.correct).map(a => a.key)
  }, [quiz.choices])

  const resolvedCorrectKeySet = useMemo(() => {
    const set = new Set<string>()
    if (!solveResult?.correctAnswers || solveResult.correctAnswers.length === 0) {
      choiceCorrectKeys.forEach(k => set.add(k))
      return set
    }

    const allChoiceKeys = quiz.choices.map(c => c.key)

    solveResult.correctAnswers.forEach(ca => {
      if (allChoiceKeys.includes(ca)) {
        set.add(ca)
        return
      }
      const posIdx = parseInt(ca, 10)
      if (!isNaN(posIdx)) {
        if (posIdx >= 1 && posIdx <= quiz.choices.length) {
          set.add(quiz.choices[posIdx - 1].key)
        }
      }
    })

    if (set.size === 0) {
      choiceCorrectKeys.forEach(k => set.add(k))
    }

    return set
  }, [solveResult?.correctAnswers, quiz.choices, choiceCorrectKeys])

  const computedInitialKeys = useMemo(() => {
    if (hasInitialAnswers) {
      return JSON.parse(initialAnswersKey) as string[]
    }
    if (isViewSolved) {
      return JSON.parse(correctAnswersKey) as string[]
    }
    return []
  }, [correctAnswersKey, hasInitialAnswers, initialAnswersKey, isViewSolved])

  const [selectedKeys, setSelectedKeys] = useState<string[]>(computedInitialKeys)

  useEffect(() => {
    setSelectedKeys(prev => {
      if (prev.length === computedInitialKeys.length && prev.every((v, i) => v === computedInitialKeys[i])) {
        return prev
      }
      return computedInitialKeys
    })
  }, [computedInitialKeys])

  const lastEmittedAnswersRef = useRef<string[] | null>(null)
  useEffect(() => {
    const last = lastEmittedAnswersRef.current
    if (last && last.length === selectedKeys.length && last.every((v, i) => v === selectedKeys[i])) {
      return
    }
    lastEmittedAnswersRef.current = selectedKeys
    onAnswerChange?.(selectedKeys)
  }, [selectedKeys, onAnswerChange])

  const correctsBySelectedKey = useMemo(() => {
    const map = new Map<string, boolean | null>()
    const submittedKeys = initialAnswersProp ?? selectedKeys
    if (solveResult?.corrects && submittedKeys.length > 0) {
      submittedKeys.forEach((key, idx) => {
        if (idx < solveResult.corrects.length) {
          map.set(key, solveResult.corrects[idx])
        }
      })
    }
    return map
  }, [solveResult?.corrects, initialAnswersProp, selectedKeys])

  const canSubmit = selectedKeys.length > 0

  const gridContainerId = useMemo(() => `multi-choice-grid-${quizId}`, [quizId])

  const handleSelect = useCallback((key: string) => {
    if (isSolved || isViewSolved) return
    setSelectedKeys(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key)
      }
      return [...prev, key]
    })
  }, [isSolved, isViewSolved])

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, selectedKeys, quiz.vars, elapsedTime)
  }, [selectedKeys, canSubmit, isSolved, startTime, onSolve, quizId, quiz.vars])

  return {
    selectedKeys,
    sortedAnswers,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    answerCount,
    resolvedCorrectKeySet,
    correctsBySelectedKey,
    gridContainerId,
    handleSelect,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
